"use client";

import React, { useMemo, useRef, useCallback } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { VirtualCCTTableProps } from './cct-types';
import { useCCTDataShaping, useColorPrecomputation, valueToBucketIndex, getColorBucketClass, getCellColor, CCT_DEFAULTS } from './cct-utils';
import './cct-colors.css';

// Main virtualized CCT table component
export default function VirtualCCTTable({
  data,
  appearance,
  widget,
}: VirtualCCTTableProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Merge defaults with appearance
  const config = useMemo(() => ({
    ...CCT_DEFAULTS,
    ...appearance,
  }), [appearance]);

  // Data shaping
  const { rows, columns } = useCCTDataShaping(data);

  // Color precomputation
  const { measureMaxima } = useColorPrecomputation(data, appearance.cctColoringScope || 'per-measure');

  // Separate factor and measure columns
  const factorColumns = columns.filter(col => col.id.startsWith('factor'));
  const measureColumns = columns.filter(col => !col.id.startsWith('factor'));

  // Format cell value function
  const formatCellValue = useCallback((value: number | null): string => {
    if (value === null) return "-";
    if (typeof value === 'number') {
      switch (appearance.numberFormat) {
        case 'rowPct':
        case 'colPct':
        case 'grandPct':
          return value.toLocaleString();
        default:
          return value.toLocaleString();
      }
    }
    return String(value);
  }, [appearance.numberFormat]);

  // Virtualizer for rows only (simpler approach)
  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollContainerRef.current,
    estimateSize: () => config.rowHeight,
    overscan: config.rowOverscan,
  });

  // Virtual items
  const virtualRows = rowVirtualizer.getVirtualItems();
  const totalRowHeight = rowVirtualizer.getTotalSize();

  // Cell padding based on appearance
  const cellPadding = appearance.compactDensity ? 'px-2 py-1' : 'px-4 py-2';
  const showBorders = appearance.cellBorder !== false;

  return (
    <div className="h-full w-full overflow-x-auto overflow-y-auto relative" ref={scrollContainerRef}>
      {/* Scroll indicator for horizontal scrolling on mobile */}
      <div className="absolute top-0 right-0 bg-gradient-to-l from-white to-transparent w-8 h-full pointer-events-none z-10 sm:hidden" />
      <table className={`w-full text-xs sm:text-sm ${showBorders ? 'border-collapse border border-gray-300' : ''}`}>
        <thead>
          <tr>
            {factorColumns.map((factor, i) => (
              <th key={i} className={`${cellPadding} text-center font-medium bg-gray-50 ${showBorders ? 'border border-gray-300' : ''}`}>
                {factor.header}
              </th>
            ))}
            {measureColumns.map((measure, i) => (
              <th key={i} className={`${cellPadding} text-center font-medium bg-gray-50 ${showBorders ? 'border border-gray-300' : ''}`}>
                {measure.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {/* Spacer for virtual rows above viewport */}
          {virtualRows.length > 0 && (
            <tr style={{ height: virtualRows[0].start }} />
          )}

          {/* Rendered virtual rows */}
          {virtualRows.map((virtualRow) => {
            const row = rows[virtualRow.index];
            return (
              <tr key={row.id}>
                {factorColumns.map((factor, factorIndex) => (
                  <td key={factor.id} className={`${cellPadding} font-medium bg-gray-50 ${showBorders ? 'border border-gray-300' : ''}`}>
                    {row[factor.accessorKey]}
                  </td>
                ))}
                {measureColumns.map((measure, measureIndex) => {
                  const value = row[measure.accessorKey] as number | null;
                  const color = getCellColor(virtualRow.index, measureIndex, value, appearance, data.measures, data.values);
                  return (
                    <td
                      key={measure.id}
                      className={`${cellPadding} text-center ${showBorders ? 'border border-gray-300' : ''}`}
                      style={{ color }}
                    >
                      {formatCellValue(value)}
                    </td>
                  );
                })}
              </tr>
            );
          })}

          {/* Spacer for virtual rows below viewport */}
          {virtualRows.length > 0 && (
            <tr style={{ height: totalRowHeight - virtualRows[virtualRows.length - 1].end }} />
          )}
        </tbody>
      </table>
    </div>
  );
}
